/**
 * WebRTC Service for P2P Voice and Video Call Media Handling
 * SignalR is used solely for SDP & ICE Candidate signaling.
 */

const DEFAULT_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
  { urls: 'stun:stun2.l.google.com:19302' },
  { urls: 'stun:stun3.l.google.com:19302' },
  { urls: 'stun:stun4.l.google.com:19302' }
];

export class WebRTCService {
  constructor(options = {}) {
    this.iceServers = options.iceServers || DEFAULT_ICE_SERVERS;
    this.onLocalStream = options.onLocalStream || (() => {});
    this.onRemoteStream = options.onRemoteStream || (() => {});
    this.onIceCandidate = options.onIceCandidate || (() => {});
    this.onConnectionStateChange = options.onConnectionStateChange || (() => {});

    this.peerConnection = null;
    this.localStream = null;
    this.remoteStream = null;
    this.pendingIceCandidates = [];
    this.isRemoteDescriptionSet = false;
    this.currentFacingMode = 'user'; // 'user' or 'environment'
  }

  /**
   * Initialize local media devices (Camera / Microphone)
   */
  async initLocalMedia(callType = 'Voice', facingMode = 'user') {
    this.currentFacingMode = facingMode;
    const isVideo = callType.toLowerCase() === 'video';

    if (!isVideo) {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: false
      });
      this.onLocalStream(this.localStream);
      return this.localStream;
    }

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const primaryVideoConstraints = isMobile
      ? { facingMode: this.currentFacingMode, width: { ideal: 1280 }, height: { ideal: 720 } }
      : { width: { ideal: 1280 }, height: { ideal: 720 } };

    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
        video: primaryVideoConstraints
      });
      this.onLocalStream(this.localStream);
      return this.localStream;
    } catch (err1) {
      console.warn('[WebRTC] Primary video constraints failed, trying basic video:true fallback for laptop/webcam...', err1);
      try {
        this.localStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true },
          video: true
        });
        this.onLocalStream(this.localStream);
        return this.localStream;
      } catch (err2) {
        console.error('[WebRTC] All video constraints failed. Fallback to audio only.', err2);
        this.localStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: false
        });
        this.onLocalStream(this.localStream);
        return this.localStream;
      }
    }
  }

  /**
   * Create RTCPeerConnection and wire up event listeners
   */
  createPeerConnection() {
    if (this.peerConnection) return this.peerConnection;

    this.peerConnection = new RTCPeerConnection({
      iceServers: this.iceServers,
      iceCandidatePoolSize: 10
    });

    this.remoteStream = new MediaStream();
    this.onRemoteStream(this.remoteStream);

    // Add local tracks to peer connection
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        this.peerConnection.addTrack(track, this.localStream);
      });
    }

    // Handle incoming remote tracks
    this.peerConnection.ontrack = (event) => {
      console.log('[WebRTC] Remote track received:', event.track?.kind);
      const stream = event.streams && event.streams[0] ? event.streams[0] : null;
      if (stream) {
        stream.getTracks().forEach(track => {
          if (!this.remoteStream.getTracks().some(t => t.id === track.id)) {
            this.remoteStream.addTrack(track);
          }
        });
      } else if (event.track) {
        if (!this.remoteStream.getTracks().some(t => t.id === event.track.id)) {
          this.remoteStream.addTrack(event.track);
        }
      }
      this.onRemoteStream(this.remoteStream);
    };

    // Handle ICE Candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.onIceCandidate(event.candidate);
      }
    };

    // Connection state monitoring
    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection?.connectionState;
      console.log('[WebRTC] Connection State Changed:', state);
      this.onConnectionStateChange(state);
    };

    this.peerConnection.oniceconnectionstatechange = () => {
      const state = this.peerConnection?.iceConnectionState;
      console.log('[WebRTC] ICE Connection State Changed:', state);
      if (state === 'failed' || state === 'disconnected') {
        this.restartIce();
      }
    };

    return this.peerConnection;
  }

  /**
   * Create SDP Offer
   */
  async createOffer() {
    this.createPeerConnection();
    const offer = await this.peerConnection.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: true
    });
    await this.peerConnection.setLocalDescription(offer);
    return offer;
  }

  /**
   * Create SDP Answer after setting remote offer
   */
  async createAnswer(offerSdp) {
    this.createPeerConnection();
    const offerDesc = new RTCSessionDescription(
      typeof offerSdp === 'string' ? JSON.parse(offerSdp) : offerSdp
    );
    await this.peerConnection.setRemoteDescription(offerDesc);
    this.isRemoteDescriptionSet = true;
    await this.processBufferedIceCandidates();

    const answer = await this.peerConnection.createAnswer();
    await this.peerConnection.setLocalDescription(answer);
    return answer;
  }

  /**
   * Set Remote Answer SDP
   */
  async setRemoteAnswer(answerSdp) {
    if (!this.peerConnection) return;
    const answerDesc = new RTCSessionDescription(
      typeof answerSdp === 'string' ? JSON.parse(answerSdp) : answerSdp
    );
    await this.peerConnection.setRemoteDescription(answerDesc);
    this.isRemoteDescriptionSet = true;
    await this.processBufferedIceCandidates();
  }

  /**
   * Add Remote ICE Candidate with buffering
   */
  async addIceCandidate(candidateObj) {
    const candidate = new RTCIceCandidate(
      typeof candidateObj === 'string' ? JSON.parse(candidateObj) : candidateObj
    );

    if (this.peerConnection && this.isRemoteDescriptionSet) {
      try {
        await this.peerConnection.addIceCandidate(candidate);
      } catch (err) {
        console.warn('[WebRTC] Failed to add ICE Candidate:', err);
      }
    } else {
      this.pendingIceCandidates.push(candidate);
    }
  }

  /**
   * Process any ICE candidates received prior to remote SDP set
   */
  async processBufferedIceCandidates() {
    while (this.pendingIceCandidates.length > 0) {
      const candidate = this.pendingIceCandidates.shift();
      try {
        await this.peerConnection.addIceCandidate(candidate);
      } catch (err) {
        console.warn('[WebRTC] Failed adding buffered ICE Candidate:', err);
      }
    }
  }

  /**
   * Toggle Audio (Microphone) Mute / Unmute
   */
  setAudioEnabled(enabled) {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach(track => {
        track.enabled = enabled;
      });
    }
  }

  /**
   * Toggle Video (Camera) On / Off
   */
  setVideoEnabled(enabled) {
    if (this.localStream) {
      this.localStream.getVideoTracks().forEach(track => {
        track.enabled = enabled;
      });
    }
  }

  /**
   * Switch between Front and Rear facing camera (for Mobile/Android)
   */
  async switchCamera(callType = 'Video') {
    const nextFacingMode = this.currentFacingMode === 'user' ? 'environment' : 'user';
    console.log(`[WebRTC] Switching camera from ${this.currentFacingMode} to ${nextFacingMode}`);

    if (this.localStream) {
      this.localStream.getVideoTracks().forEach(t => {
        t.stop();
        this.localStream.removeTrack(t);
      });
    }

    try {
      const newVideoStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: nextFacingMode, width: { ideal: 1280 }, height: { ideal: 720 } }
      });

      const newVideoTrack = newVideoStream.getVideoTracks()[0];
      if (newVideoTrack) {
        this.localStream.addTrack(newVideoTrack);
        this.currentFacingMode = nextFacingMode;

        // Replace track in peer connection senders
        if (this.peerConnection) {
          const sender = this.peerConnection.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            await sender.replaceTrack(newVideoTrack);
          } else {
            this.peerConnection.addTrack(newVideoTrack, this.localStream);
          }
        }
      }
      this.onLocalStream(this.localStream);
      return this.currentFacingMode;
    } catch (err) {
      console.error('[WebRTC] Camera Switch Failed:', err);
      return this.currentFacingMode;
    }
  }

  /**
   * Restart ICE connection on temporary network drop
   */
  async restartIce() {
    if (this.peerConnection && typeof this.peerConnection.restartIce === 'function') {
      console.log('[WebRTC] Triggering ICE restart...');
      this.peerConnection.restartIce();
    }
  }

  /**
   * Evaluate Connection Quality
   */
  async getConnectionQuality() {
    if (!this.peerConnection) return 'Disconnected';
    const state = this.peerConnection.connectionState;
    if (state === 'connected') return 'Excellent';
    if (state === 'connecting') return 'Connecting';
    if (state === 'disconnected') return 'Poor';
    return 'Disconnected';
  }

  /**
   * Clean up media tracks and peer connection
   */
  destroy() {
    console.log('[WebRTC] Destroying WebRTC Service...');
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }
    if (this.remoteStream) {
      this.remoteStream.getTracks().forEach(track => track.stop());
      this.remoteStream = null;
    }
    if (this.peerConnection) {
      this.peerConnection.onicecandidate = null;
      this.peerConnection.ontrack = null;
      this.peerConnection.onconnectionstatechange = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }
    this.pendingIceCandidates = [];
    this.isRemoteDescriptionSet = false;
  }
}
